/**
 * Lightweight App Insights telemetry for the dashboard SPA.
 *
 * Tracks page views, user interactions, and performance metrics.
 * Opt-out: set VITE_TELEMETRY_DISABLED=true in the env file.
 */
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

let appInsights: ApplicationInsights | null = null;

/** Global properties stamped on every event. */
const globalProperties: Record<string, string> = {};

/** Initialize App Insights. Call once at app startup. */
export function initTelemetry(): void {
    const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;
    if (!connectionString || import.meta.env.VITE_TELEMETRY_DISABLED === "true") {
        return;
    }

    appInsights = new ApplicationInsights({
        config: {
            connectionString,
            enableAutoRouteTracking: true,
            disableFetchTracking: false,
            enableCorsCorrelation: false,
            // Don't track every XHR/fetch — we'll track API calls selectively
            disableAjaxTracking: true,
            // Reduce noise: no unhandled promise tracking
            enableUnhandledPromiseRejectionTracking: false,
        },
    });

    appInsights.loadAppInsights();
}

/** Track a custom event with optional properties and measurements. */
export function trackEvent(
    name: string,
    properties?: Record<string, string>,
    measurements?: Record<string, number>,
): void {
    appInsights?.trackEvent({ name }, { ...globalProperties, ...properties, ...measurements });
}

/** Track a page view manually (supplement to auto-route tracking). */
export function trackPageView(name: string, uri?: string): void {
    appInsights?.trackPageView({ name, uri, properties: { ...globalProperties } });
}

/** Track an exception. */
export function trackException(error: Error, properties?: Record<string, string>): void {
    appInsights?.trackException({ exception: error }, { ...globalProperties, ...properties });
}

/** Track a metric (e.g. API response time). */
export function trackMetric(name: string, value: number, properties?: Record<string, string>): void {
    appInsights?.trackMetric({ name, average: value }, { ...globalProperties, ...properties });
}

/**
 * Set persistent context properties stamped on all subsequent events.
 * Call when resourceId/projectId become available.
 */
export function setTelemetryContext(ctx: { subscriptionId?: string; resourceId?: string; projectId?: string }): void {
    if (ctx.subscriptionId) globalProperties.subscriptionId = ctx.subscriptionId;
    if (ctx.resourceId) globalProperties.resourceId = ctx.resourceId;
    if (ctx.projectId) globalProperties.projectId = ctx.projectId;
}

/** Extract subscription ID from an ARM resource ID. */
export function extractSubscriptionId(armResourceId: string): string | undefined {
    const match = armResourceId.match(/\/subscriptions\/([^/]+)/i);
    return match?.[1];
}

/** Set authenticated user context (hashed email or ID). */
export function setAuthenticatedUser(userId: string): void {
    appInsights?.setAuthenticatedUserContext(userId, undefined, true);
}

/** Flush pending telemetry (call before page unload if needed). */
export function flushTelemetry(): void {
    appInsights?.flush();
}
